import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  ArrowLeft,
  Save,
  RefreshCw,
  AlertTriangle,
  Wrench,
  Palette,
  Type,
  Image,
  Code
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { maintenanceService } from '../../services/maintenanceService';
import { useAuth } from '../../contexts/AuthContext';

// Função para verificar se o usuário é desenvolvedor
const isDeveloper = (email: string | null | undefined): boolean => {
  return email === 'dev@playkids.com';
};

// Função debounce para evitar muitas chamadas ao Firebase
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

interface MaintenanceSettings {
  maintenanceMode: boolean;
  maintenanceMessage: string;
  maintenanceTime: string;
  maintenanceContact: string;
  maintenanceTitle: string;
  maintenanceSubtitle: string;
  maintenanceBackgroundColor: string;
  maintenanceCardColor: string;
  maintenanceTextColor: string;
  maintenanceAccentColor: string;
  maintenanceLogoUrl: string;
  maintenanceAnimationEnabled: boolean;
  maintenanceCustomCss: string;
}

const defaultSettings: MaintenanceSettings = {
  maintenanceMode: false,
  maintenanceMessage: 'Sistema em manutenção programada',
  maintenanceTime: '2-4 horas',
  maintenanceContact: 'contato@playkidseventos.com.br',
  maintenanceTitle: 'Sistema em Manutenção',
  maintenanceSubtitle: 'Estamos trabalhando para melhorar sua experiência',
  maintenanceBackgroundColor: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
  maintenanceCardColor: 'rgba(255, 255, 255, 0.1)',
  maintenanceTextColor: '#ffffff',
  maintenanceAccentColor: '#f59e0b',
  maintenanceLogoUrl: '/images/LOGO.png',
  maintenanceAnimationEnabled: true,
  maintenanceCustomCss: ''
};

// Temas temáticos pré-prontos
const themes = {
  dark: {
    name: 'Escuro Clássico',
    description: 'Tema escuro elegante e profissional',
    backgroundColor: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
    cardColor: 'rgba(255, 255, 255, 0.1)',
    textColor: '#ffffff',
    accentColor: '#f59e0b',
    title: 'Sistema em Manutenção',
    subtitle: 'Estamos trabalhando para melhorar sua experiência'
  },
  light: {
    name: 'Claro Moderno',
    description: 'Tema claro e minimalista',
    backgroundColor: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f8fafc 100%)',
    cardColor: 'rgba(255, 255, 255, 0.8)',
    textColor: '#1e293b',
    accentColor: '#3b82f6',
    title: 'Sistema em Manutenção',
    subtitle: 'Estamos trabalhando para melhorar sua experiência'
  },
  ocean: {
    name: 'Oceano',
    description: 'Tema inspirado no oceano com tons azuis',
    backgroundColor: 'linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #0f172a 100%)',
    cardColor: 'rgba(59, 130, 246, 0.1)',
    textColor: '#ffffff',
    accentColor: '#06b6d4',
    title: 'Sistema em Manutenção',
    subtitle: 'Estamos trabalhando para melhorar sua experiência'
  },
  sunset: {
    name: 'Pôr do Sol',
    description: 'Tema quente com tons de laranja e rosa',
    backgroundColor: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 50%, #dc2626 100%)',
    cardColor: 'rgba(251, 146, 60, 0.1)',
    textColor: '#ffffff',
    accentColor: '#fbbf24',
    title: 'Sistema em Manutenção',
    subtitle: 'Estamos trabalhando para melhorar sua experiência'
  },
  forest: {
    name: 'Floresta',
    description: 'Tema natural com tons verdes',
    backgroundColor: 'linear-gradient(135deg, #064e3b 0%, #059669 50%, #064e3b 100%)',
    cardColor: 'rgba(16, 185, 129, 0.1)',
    textColor: '#ffffff',
    accentColor: '#10b981',
    title: 'Sistema em Manutenção',
    subtitle: 'Estamos trabalhando para melhorar sua experiência'
  },
  purple: {
    name: 'Roxo Místico',
    description: 'Tema roxo elegante e moderno',
    backgroundColor: 'linear-gradient(135deg, #581c87 0%, #7c3aed 50%, #581c87 100%)',
    cardColor: 'rgba(168, 85, 247, 0.1)',
    textColor: '#ffffff',
    accentColor: '#a855f7',
    title: 'Sistema em Manutenção',
    subtitle: 'Estamos trabalhando para melhorar sua experiência'
  },
  neon: {
    name: 'Neon Cyber',
    description: 'Tema futurista com cores neon',
    backgroundColor: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%)',
    cardColor: 'rgba(0, 255, 127, 0.1)',
    textColor: '#00ff7f',
    accentColor: '#00ff7f',
    title: 'SISTEMA EM MANUTENÇÃO',
    subtitle: 'ATUALIZAÇÃO EM ANDAMENTO...'
  },
  minimal: {
    name: 'Minimalista',
    description: 'Tema limpo e simples',
    backgroundColor: '#ffffff',
    cardColor: 'rgba(0, 0, 0, 0.05)',
    textColor: '#374151',
    accentColor: '#6b7280',
    title: 'Manutenção',
    subtitle: 'Voltamos em breve'
  }
};

const MaintenanceSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<MaintenanceSettings>(defaultSettings);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Ref para controlar o foco dos inputs (genérico para input e textarea)
  const focusedInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const isUpdatingRef = useRef(false);

  // Função para manter o foco no input
  const maintainFocus = useCallback(() => {
    if (focusedInputRef.current && !isUpdatingRef.current) {
      // Usar setTimeout para garantir que o foco seja mantido após re-renderização
      setTimeout(() => {
        if (focusedInputRef.current) {
          focusedInputRef.current.focus();
          // Mover cursor para o final do texto (apenas para inputs)
          if (focusedInputRef.current instanceof HTMLInputElement) {
            const length = focusedInputRef.current.value.length;
            focusedInputRef.current.setSelectionRange(length, length);
          }
        }
      }, 0);
    }
  }, []);

  // useEffect para manter o foco após re-renderização
  useEffect(() => {
    maintainFocus();
  });

  // Função para configurar o foco nos inputs (genérico)
  const setupInputFocus = useCallback((inputRef: HTMLInputElement | HTMLTextAreaElement | null) => {
    if (inputRef) {
      focusedInputRef.current = inputRef;
      inputRef.addEventListener('focus', () => {
        focusedInputRef.current = inputRef;
      });
    }
  }, []);

  // Carregar configurações iniciais
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const maintenanceRef = doc(db, 'system', 'maintenance');
        const docSnap = await getDoc(maintenanceRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setSettings({
            maintenanceMode: data.enabled === true,
            maintenanceMessage: data.message || 'Sistema em manutenção programada',
            maintenanceTime: data.estimatedTime || '2-4 horas',
            maintenanceContact: data.contact || 'contato@playkidseventos.com.br',
            maintenanceTitle: data.title || 'Sistema em Manutenção',
            maintenanceSubtitle: data.subtitle || 'Estamos trabalhando para melhorar sua experiência',
            maintenanceBackgroundColor: data.backgroundColor || 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
            maintenanceCardColor: data.cardColor || 'rgba(255, 255, 255, 0.1)',
            maintenanceTextColor: data.textColor || '#ffffff',
            maintenanceAccentColor: data.accentColor || '#f59e0b',
            maintenanceLogoUrl: data.logoUrl || '/images/LOGO.png',
            maintenanceAnimationEnabled: data.animationEnabled !== false,
            maintenanceCustomCss: data.customCss || ''
          });
        }
      } catch (error) {
        console.error('Erro ao carregar configurações de manutenção:', error);
      }
    };
    
    loadSettings();
  }, []);

  // Detectar mudanças
  useEffect(() => {
    const savedSettings = localStorage.getItem('maintenance_settings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setHasChanges(JSON.stringify(settings) !== JSON.stringify(parsed));
    } else {
      setHasChanges(JSON.stringify(settings) !== JSON.stringify(defaultSettings));
    }
  }, [settings]);

  const handleSettingChange = useCallback((key: keyof MaintenanceSettings, value: any) => {
    isUpdatingRef.current = true;
    
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      
      // Detectar mudanças manualmente
      const savedSettings = localStorage.getItem('maintenance_settings');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(parsed));
      } else {
        setHasChanges(JSON.stringify(newSettings) !== JSON.stringify(defaultSettings));
      }
      
      return newSettings;
    });
    
    // Resetar flag após um pequeno delay
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 10);
  }, []);

  // Função para aplicar um tema e salvar automaticamente
  const applyTheme = useCallback(async (themeKey: keyof typeof themes) => {
    const theme = themes[themeKey];
    
    const newSettings = {
      ...settings,
      maintenanceTitle: theme.title,
      maintenanceSubtitle: theme.subtitle,
      maintenanceBackgroundColor: theme.backgroundColor,
      maintenanceCardColor: theme.cardColor,
      maintenanceTextColor: theme.textColor,
      maintenanceAccentColor: theme.accentColor
    };
    
    setSettings(newSettings);
    setHasChanges(false);
    
    try {
      // Salvar no localStorage
      localStorage.setItem('maintenance_settings', JSON.stringify(newSettings));
      
      // Salvar configurações de manutenção no Firebase
      await maintenanceService.updateMaintenanceSettings({
        enabled: newSettings.maintenanceMode,
        message: newSettings.maintenanceMessage,
        estimatedTime: newSettings.maintenanceTime,
        contact: newSettings.maintenanceContact,
        title: newSettings.maintenanceTitle,
        subtitle: newSettings.maintenanceSubtitle,
        backgroundColor: newSettings.maintenanceBackgroundColor,
        cardColor: newSettings.maintenanceCardColor,
        textColor: newSettings.maintenanceTextColor,
        accentColor: newSettings.maintenanceAccentColor,
        logoUrl: newSettings.maintenanceLogoUrl,
        animationEnabled: newSettings.maintenanceAnimationEnabled,
        customCss: newSettings.maintenanceCustomCss
      });
      
    } catch (error) {
      console.error('❌ Erro ao salvar tema:', error);
    }
  }, [settings]);

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Salvar no localStorage
      localStorage.setItem('maintenance_settings', JSON.stringify(settings));
      
      // Salvar configurações de manutenção no Firebase
      await maintenanceService.updateMaintenanceSettings({
        enabled: settings.maintenanceMode,
        message: settings.maintenanceMessage,
        estimatedTime: settings.maintenanceTime,
        contact: settings.maintenanceContact,
        title: settings.maintenanceTitle,
        subtitle: settings.maintenanceSubtitle,
        backgroundColor: settings.maintenanceBackgroundColor,
        cardColor: settings.maintenanceCardColor,
        textColor: settings.maintenanceTextColor,
        accentColor: settings.maintenanceAccentColor,
        logoUrl: settings.maintenanceLogoUrl,
        animationEnabled: settings.maintenanceAnimationEnabled,
        customCss: settings.maintenanceCustomCss
      });
      
      setHasChanges(false);
      
      // Simular delay de salvamento
      await new Promise(resolve => setTimeout(resolve, 1000));
      
    } catch (error) {
      console.error('❌ Erro ao salvar configurações:', error);
      alert('Erro ao salvar configurações. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnableMaintenance = async () => {
    try {
      await maintenanceService.enableMaintenance({
        message: settings.maintenanceMessage,
        estimatedTime: settings.maintenanceTime,
        contact: settings.maintenanceContact,
        title: settings.maintenanceTitle,
        subtitle: settings.maintenanceSubtitle,
        backgroundColor: settings.maintenanceBackgroundColor,
        cardColor: settings.maintenanceCardColor,
        textColor: settings.maintenanceTextColor,
        accentColor: settings.maintenanceAccentColor,
        logoUrl: settings.maintenanceLogoUrl,
        animationEnabled: settings.maintenanceAnimationEnabled,
        customCss: settings.maintenanceCustomCss
      });
    } catch (error) {
      console.error('Erro ao ativar manutenção:', error);
      alert('Erro ao ativar modo manutenção.');
    }
  };

  const handleDisableMaintenance = async () => {
    try {
      await maintenanceService.disableMaintenance();
    } catch (error) {
      console.error('Erro ao desativar manutenção:', error);
      alert('Erro ao desativar modo manutenção.');
    }
  };

  // Verificar se o usuário tem permissão de desenvolvedor
  if (!isDeveloper(currentUser?.email)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-pink-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Acesso Negado
          </h2>
          <p className="text-slate-600 mb-6">
            Você não tem permissão para acessar esta área. Apenas desenvolvedores autorizados podem acessar estas funcionalidades.
          </p>
          <button
            onClick={() => window.history.back()}
            className="w-full bg-slate-200 text-slate-700 px-6 py-3 rounded-lg hover:bg-slate-300 transition-colors font-medium"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Wrench className="w-6 h-6 text-blue-600" />
              Configurações de Manutenção
            </h1>
            <p className="text-slate-600">Personalize a tela de manutenção do sistema</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {hasChanges && (
            <div className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Mudanças não salvas</span>
            </div>
          )}
          
          <button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSaving ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </div>

      {/* Quick Controls */}
      <div className="bg-slate-50 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-slate-900 mb-3">Controles Rápidos</h3>
        <div className="flex gap-3">
          <button
            onClick={handleEnableMaintenance}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            Ativar Manutenção
          </button>
          <button
            onClick={handleDisableMaintenance}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Desativar Manutenção
          </button>
        </div>
      </div>

      {/* Temas Pré-prontos */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Palette className="w-5 h-5 text-blue-600" />
          Temas Pré-prontos
        </h3>
        <p className="text-slate-600 mb-6">
          Escolha um tema pré-configurado para personalizar rapidamente a aparência da página de manutenção.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(themes).map(([key, theme]) => (
            <div
              key={key}
              className="relative group cursor-pointer rounded-lg border-2 border-slate-200 hover:border-blue-500 transition-all duration-200 overflow-hidden"
              onClick={() => applyTheme(key as keyof typeof themes)}
            >
              {/* Preview do tema */}
              <div 
                className="h-24 w-full"
                style={{ background: theme.backgroundColor }}
              >
                <div className="p-3 h-full flex flex-col justify-center">
                  <div 
                    className="text-sm font-semibold mb-1 truncate"
                    style={{ color: theme.textColor }}
                  >
                    {theme.title}
                  </div>
                  <div 
                    className="text-xs opacity-80 truncate"
                    style={{ color: theme.textColor }}
                  >
                    {theme.subtitle}
                  </div>
                </div>
              </div>
              
              {/* Informações do tema */}
              <div className="p-3 bg-white">
                <h4 className="font-medium text-slate-900 text-sm">{theme.name}</h4>
                <p className="text-xs text-slate-500 mt-1">{theme.description}</p>
              </div>
              
              {/* Overlay de hover */}
              <div className="absolute inset-0 bg-blue-500 bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Aplicar Tema
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Settings Form */}
      <div className="space-y-8">
        {/* Informações Básicas */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Type className="w-5 h-5 text-blue-600" />
            Informações Básicas
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Mensagem de Manutenção
              </label>
              <input
                ref={setupInputFocus}
                type="text"
                value={settings.maintenanceMessage}
                onChange={(e) => handleSettingChange('maintenanceMessage', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Sistema em manutenção programada"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Tempo Estimado
              </label>
              <input
                ref={setupInputFocus}
                type="text"
                value={settings.maintenanceTime}
                onChange={(e) => handleSettingChange('maintenanceTime', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="2-4 horas"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Contato
              </label>
              <input
                ref={setupInputFocus}
                type="email"
                value={settings.maintenanceContact}
                onChange={(e) => handleSettingChange('maintenanceContact', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="contato@playkidseventos.com.br"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Título
              </label>
              <input
                ref={setupInputFocus}
                type="text"
                value={settings.maintenanceTitle}
                onChange={(e) => handleSettingChange('maintenanceTitle', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Sistema em Manutenção"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Subtítulo
              </label>
              <input
                ref={setupInputFocus}
                type="text"
                value={settings.maintenanceSubtitle}
                onChange={(e) => handleSettingChange('maintenanceSubtitle', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Estamos trabalhando para melhorar sua experiência"
              />
            </div>
          </div>
        </div>

        {/* Aparência */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5 text-blue-600" />
            Aparência
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                URL da Logo
              </label>
              <input
                ref={setupInputFocus}
                type="url"
                value={settings.maintenanceLogoUrl}
                onChange={(e) => handleSettingChange('maintenanceLogoUrl', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="/images/LOGO.png"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="font-medium text-slate-700">Animações</label>
                <p className="text-sm text-slate-500">Ativar animações na tela de manutenção</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.maintenanceAnimationEnabled}
                  onChange={(e) => handleSettingChange('maintenanceAnimationEnabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Cor de Fundo
                </label>
                <input
                  ref={setupInputFocus}
                  type="text"
                  value={settings.maintenanceBackgroundColor}
                  onChange={(e) => handleSettingChange('maintenanceBackgroundColor', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Cor do Card
                </label>
                <input
                  ref={setupInputFocus}
                  type="text"
                  value={settings.maintenanceCardColor}
                  onChange={(e) => handleSettingChange('maintenanceCardColor', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="rgba(255, 255, 255, 0.1)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Cor do Texto
                </label>
                <input
                  ref={setupInputFocus}
                  type="text"
                  value={settings.maintenanceTextColor}
                  onChange={(e) => handleSettingChange('maintenanceTextColor', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="#ffffff"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Cor de Destaque
                </label>
                <input
                  ref={setupInputFocus}
                  type="text"
                  value={settings.maintenanceAccentColor}
                  onChange={(e) => handleSettingChange('maintenanceAccentColor', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="#f59e0b"
                />
              </div>
            </div>
          </div>
        </div>

        {/* CSS Customizado */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Code className="w-5 h-5 text-blue-600" />
            CSS Customizado
          </h3>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              CSS Customizado
            </label>
            <textarea
              ref={setupInputFocus}
              value={settings.maintenanceCustomCss}
              onChange={(e) => handleSettingChange('maintenanceCustomCss', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-32 font-mono text-sm"
              placeholder="/* CSS customizado para a página de manutenção */
.maintenance-custom {
  /* Suas regras CSS aqui */
}"
            />
            <p className="text-xs text-slate-500 mt-1">
              CSS customizado será aplicado à página de manutenção
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaintenanceSettings;
